/**
 * adminApi.ts — shared types and fetch helper for all /api/admin/* routes.
 *
 * Design notes:
 * - Never throws on HTTP errors; always returns { ok, status, data }.
 * - Tolerate empty or non-JSON bodies (e.g. 204, malformed 500s).
 * - On missing token returns { ok: false, status: 0, data: { error: 'no_token' } }.
 * - credentials: 'omit' per security contract (same-origin; no cross-origin cookies).
 */

export interface AllowlistEntry {
  id: string;
  type: 'domain' | 'email';
  value: string;
  enabled: boolean;
  label: string | null;
  addedBy: string | null;
  addedAt: string | null;   // ISO or null
  updatedAt: string | null; // ISO or null
  expiresAt: string | null; // ISO or null
  expired: boolean;
}

export interface SerializedActivity {
  id: string;
  ts: string | null;          // ISO datetime or null
  type: 'login' | 'pageview' | 'dwell';
  uid: string;
  email: string;
  isStaff: boolean;
  path: string | null;
  dwellMs: number | null;
  ip: string;
  ua: string;
}

export interface AdminFetchResult {
  ok: boolean;
  status: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
}

/**
 * Reusable fetch helper for all admin API calls.
 *
 * @param getIdToken  - from useAuth(); returns a fresh Firebase ID token or null.
 * @param method      - HTTP verb.
 * @param path        - relative path, e.g. "/api/admin/allowlist" or "/api/admin/allowlist/abc123".
 * @param body        - optional request body (will be JSON-encoded).
 */
export async function adminFetch(
  getIdToken: () => Promise<string | null>,
  method: string,
  path: string,
  body?: Record<string, unknown>,
): Promise<AdminFetchResult> {
  let token: string | null;
  try {
    token = await getIdToken();
  } catch (err) {
    // getIdToken can reject on a token-refresh network error — never let that
    // become an unhandled rejection; the helper always resolves to a result.
    const message = err instanceof Error ? err.message : 'token error';
    return { ok: false, status: 0, data: { error: 'token_error', message } };
  }
  if (!token) {
    return { ok: false, status: 0, data: { error: 'no_token' } };
  }

  const init: RequestInit = {
    method,
    credentials: 'omit',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  try {
    const res = await fetch(path, init);
    const text = await res.text();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let data: any;
    try {
      data = text.length > 0 ? JSON.parse(text) : {};
    } catch {
      data = { _raw: text };
    }
    return { ok: res.ok, status: res.status, data };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, data: { error: 'network', message } };
  }
}
